import type { NewsletterIssue } from '../types/newsletter'

export function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim()
}

export function truncate(text: string, max: number): string {
  if (text.length <= max) return text
  const cut = text.slice(0, max)
  return `${cut.slice(0, cut.lastIndexOf(' ')).trim()}…`
}

export function formatIssueDate(pubDate: string): string {
  const date = parsePubDate(pubDate)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

// Handles both RSS pubDate format ("Tue, 07 Oct 2026 12:00:00 +0000")
// and the proxy's format ("2026-10-07 12:00:00").
function parsePubDate(pubDate: string): Date {
  const native = new Date(pubDate)
  if (!Number.isNaN(native.getTime())) return native
  return new Date(pubDate.trim().replace(' ', 'T'))
}

function issueTime(pubDate: string): number {
  const time = parsePubDate(pubDate).getTime()
  return Number.isNaN(time) ? 0 : time
}

// Feeds don't guarantee order, so always sort newest-first before limiting.
export function newestFirst(
  issues: NewsletterIssue[],
  limit: number,
): NewsletterIssue[] {
  return [...issues]
    .sort((a, b) => issueTime(b.pubDate) - issueTime(a.pubDate))
    .slice(0, limit)
}

export function parseRssXml(xml: string, limit: number): NewsletterIssue[] {
  const doc = new DOMParser().parseFromString(xml, 'application/xml')
  if (doc.querySelector('parsererror')) throw new Error('Invalid feed')
  const issues = Array.from(doc.querySelectorAll('item'))
    .map((item) => {
      const text = (tag: string) =>
        item.getElementsByTagName(tag)[0]?.textContent ?? ''
      const enclosure = item.querySelector('enclosure')?.getAttribute('url')
      const media = item
        .getElementsByTagName('media:content')[0]
        ?.getAttribute('url')
      return {
        title: text('title').trim(),
        link: text('link').trim(),
        pubDate: text('pubDate'),
        excerpt: truncate(stripHtml(text('description')), 180),
        thumbnail: enclosure ?? media ?? undefined,
      }
    })
  return newestFirst(issues, limit)
}

type Rss2JsonItem = {
  title: string
  link: string
  pubDate: string
  description: string
  thumbnail?: string
  enclosure?: { link?: string }
}

export function parseRss2Json(
  items: Rss2JsonItem[],
  limit: number,
): NewsletterIssue[] {
  const issues = items.map((item) => ({
    title: item.title.trim(),
    link: item.link,
    pubDate: item.pubDate,
    excerpt: truncate(stripHtml(item.description), 180),
    thumbnail: item.thumbnail || item.enclosure?.link || undefined,
  }))
  return newestFirst(issues, limit)
}
